import {useMutation, useQueryClient} from '@tanstack/react-query';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import campaignUpdaters from '@/api/updaters/campaign';
import {CampaignListItem} from '@/hooks/queries/use-campaigns-v2-list-query/useCampaignsV2ListQuery';

type QueryData = {data: CampaignListItem[]; jsonapi?: unknown} | undefined;

interface UseDeleteCampaignOptions {
  onMutate?: (campaignId: string) => void;
  onSuccess?: (campaignId: string) => void;
  onError?: (campaignId: string) => void;
}

function useDeleteCampaign(options: UseDeleteCampaignOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId: string) =>
      campaignUpdaters.deleteCampaign(campaignId),

    onMutate: async (campaignId: string) => {
      // Cancel in-flight refetches so they don't overwrite the optimistic update.
      await queryClient.cancelQueries({queryKey: ['campaignsV2List']});

      // Snapshot all matching cache entries (one per tab/state filter).
      const previousEntries = queryClient.getQueriesData<QueryData>({
        queryKey: ['campaignsV2List'],
      });

      // Optimistically remove the campaign from every cached list.
      queryClient.setQueriesData<QueryData>(
        {queryKey: ['campaignsV2List']},
        (old) =>
          old
            ? {...old, data: old.data.filter((c) => c.id !== campaignId)}
            : old
      );

      options.onMutate?.(campaignId);
      return {previousEntries};
    },

    onSuccess: (_data, campaignId) => {
      raiseFlashMessage({
        status: 'success',
        message: translate('Draft deleted successfully.'),
      });
      options.onSuccess?.(campaignId);
    },

    onError: (_err, campaignId, context) => {
      // Revert all cache entries to their pre-mutation snapshot.
      if (context?.previousEntries) {
        context.previousEntries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      raiseFlashMessage({
        status: 'error',
        message: translate('Failed to delete draft. Please try again.'),
      });
      options.onError?.(campaignId);
    },

    onSettled: () => {
      // Always re-sync with server after mutation completes or fails.
      queryClient.invalidateQueries({queryKey: ['campaignsV2List']});
    },
  });
}

export default useDeleteCampaign;
